import React, { useState, useEffect, useCallback, useMemo } from 'react';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { getJournalEntriesV2 } from '../../../api/journal';
import { useFirebase } from '../../../context/FirebaseContext';
import './MiniCalendar.css';

const MiniCalendar = ({ onViewAll, refreshTrigger = 0, isExpanded, onToggle }) => {
  const { auth } = useFirebase();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [internalCollapsed, setInternalCollapsed] = useState(true);

  const collapsed = isExpanded !== undefined ? !isExpanded : internalCollapsed;

  const handleToggle = () => {
    if (onToggle) {
      onToggle();
    } else {
      setInternalCollapsed(!internalCollapsed);
    }
  };

  const fetchEntries = useCallback(async () => {
    if (!auth?.currentUser) return;

    setLoading(true);
    try {
      await getJournalEntriesV2(auth, (result) => {
        if (result && !result.error) {
          setEntries(Array.isArray(result) ? result : []);
        }
        setLoading(false);
      }, 500, 0, 'all', null);
    } catch (error) {
      console.error('Error fetching journal entries:', error);
      setLoading(false);
    }
  }, [auth]);

  useEffect(() => {
    fetchEntries();
  }, [fetchEntries, refreshTrigger]);

  // Group entries by date
  const entriesByDate = useMemo(() => {
    const grouped = {};
    entries.forEach(entry => {
      if (!entry.trade_date) return;
      const date = new Date(entry.trade_date);
      const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
      if (!grouped[key]) {
        grouped[key] = { totalPL: 0, count: 0 };
      }
      grouped[key].totalPL += entry.profit_loss || 0;
      grouped[key].count++;
    });
    return grouped;
  }, [entries]);

  // Monthly stats
  const monthlyStats = useMemo(() => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    let totalPL = 0;
    let trades = 0;

    Object.entries(entriesByDate).forEach(([dateKey, data]) => {
      const [y, m] = dateKey.split('-').map(Number);
      if (y === year && m === month + 1) {
        totalPL += data.totalPL;
        trades += data.count;
      }
    });

    return { totalPL, trades };
  }, [entriesByDate, currentDate]);

  const getDaysInMonth = (date) => new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  const getFirstDayOfMonth = (date) => new Date(date.getFullYear(), date.getMonth(), 1).getDay();

  const navigateMonth = (dir) => {
    setCurrentDate(prev => {
      const d = new Date(prev);
      d.setMonth(prev.getMonth() + dir);
      return d;
    });
  };

  const formatPL = (value) => {
    const sign = value >= 0 ? '+' : '-';
    const abs = Math.abs(value);
    if (abs >= 1000) return `${sign}$${(abs / 1000).toFixed(1)}k`;
    return `${sign}$${abs.toFixed(0)}`;
  };

  const renderCalendar = () => {
    const daysInMonth = getDaysInMonth(currentDate);
    const firstDay = getFirstDayOfMonth(currentDate);
    const days = [];
    const today = new Date();

    // Empty cells
    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`e-${i}`} className="mini-day empty" />);
    }

    // Days
    for (let day = 1; day <= daysInMonth; day++) {
      const key = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      const data = entriesByDate[key];
      const isToday = today.getDate() === day &&
                      today.getMonth() === currentDate.getMonth() &&
                      today.getFullYear() === currentDate.getFullYear();

      let cls = 'mini-day';
      if (isToday) cls += ' today';
      if (data) cls += data.totalPL > 0 ? ' profit' : data.totalPL < 0 ? ' loss' : '';

      days.push(
        <div key={day} className={cls} title={data ? `${formatPL(data.totalPL)} (${data.count})` : ''}>
          {day}
        </div>
      );
    }

    return days;
  };

  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  return (
    <div className={`mini-calendar-panel ${collapsed ? 'collapsed' : 'expanded'}`}>
      <div className="mini-header" onClick={handleToggle}>
        <CalendarMonthIcon fontSize="small" />
        <span className="header-title">P&L Calendar</span>
        {!loading && monthlyStats.trades > 0 && (
          <div className="header-stats">
            <span className={`stat-pl ${monthlyStats.totalPL >= 0 ? 'positive' : 'negative'}`}>
              {formatPL(monthlyStats.totalPL)}
            </span>
          </div>
        )}
        {collapsed ? <ExpandMoreIcon fontSize="small" /> : <ExpandLessIcon fontSize="small" />}
      </div>

      {!collapsed && (
        <div className="mini-content">
          <div className="mini-nav">
            <button onClick={(e) => { e.stopPropagation(); navigateMonth(-1); }}>
              <ChevronLeftIcon fontSize="small" />
            </button>
            <span>{monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}</span>
            <button onClick={(e) => { e.stopPropagation(); navigateMonth(1); }}>
              <ChevronRightIcon fontSize="small" />
            </button>
          </div>

          <div className="mini-weekdays">
            {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((d, i) => (
              <div key={i}>{d}</div>
            ))}
          </div>

          <div className="mini-grid">
            {renderCalendar()}
          </div>

          {onViewAll && (
            <button className="view-all-btn" onClick={onViewAll}>
              <span>View Full Journal</span>
              <OpenInNewIcon fontSize="small" />
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default MiniCalendar;
